import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { chatActions } from 'store';
import { FindLoad } from 'utils';
import { UPLOAD_ATTACHMENT } from '../../store/chat/chat.types';
import { MentionDropdown } from './mentionDropdown';
import { EmojiPicker } from './emojiPicker';

export const MessageInput = ({ chatId, currentUserId, currentUserType, participants, onStartTyping, onStopTyping }) => {
    const dispatch = useDispatch();
    const uploadLoading = FindLoad(UPLOAD_ATTACHMENT);
    const [text, setText] = useState('');
    const [mentionActive, setMentionActive] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const [emojiOpen, setEmojiOpen] = useState(false);

    const handleTyping = useCallback(() => {
        if (onStartTyping) onStartTyping();
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            if (onStopTyping) onStopTyping();
        }, 2000);
    }, [onStartTyping, onStopTyping]);

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        };
    }, []);

    const adjustTextareaHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }, []);

    useEffect(() => {
        adjustTextareaHeight();
    }, [text, adjustTextareaHeight]);

    const getFilteredMentions = useCallback(() => {
        return (participants || [])
            .filter((p) => p.userId !== currentUserId)
            .filter((p) => {
                if (!mentionFilter) return true;
                const name = p.user ? `${p.user.firstName} ${p.user.lastName}`.toLowerCase() : '';
                return name.includes(mentionFilter.toLowerCase());
            });
    }, [participants, currentUserId, mentionFilter]);

    const handleChange = (e) => {
        const val = e.target.value;
        setText(val);
        handleTyping();

        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = val.substring(0, cursorPos);
        const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
        if (mentionMatch) {
            setMentionActive(true);
            setMentionFilter(mentionMatch[1]);
            setMentionIndex(0);
        } else {
            setMentionActive(false);
        }
    };

    const handleMentionSelect = (participant) => {
        const name = participant.user ? `${participant.user.firstName} ${participant.user.lastName}` : participant.userId;
        const cursorPos = textareaRef.current.selectionStart;
        const textBeforeCursor = text.substring(0, cursorPos);
        const textAfterCursor = text.substring(cursorPos);
        const newBefore = textBeforeCursor.replace(/@\w*$/, `@${name} `);
        setText(newBefore + textAfterCursor);
        setMentionActive(false);
        textareaRef.current.focus();
    };

    const handleAtButtonClick = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const cursorPos = textarea.selectionStart;
        const before = text.substring(0, cursorPos);
        const after = text.substring(cursorPos);
        const needsSpace = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n');
        const newText = before + (needsSpace ? ' @' : '@') + after;
        setText(newText);
        setMentionActive(true);
        setMentionFilter('');
        setMentionIndex(0);
        setTimeout(() => {
            const newPos = cursorPos + (needsSpace ? 2 : 1);
            textarea.focus();
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed) return;

        const body = {
            text: trimmed,
        };

        dispatch(chatActions.sendMessage(chatId, body));

        setText('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        if (onStopTyping) onStopTyping();
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };

    const handleKeyDown = (e) => {
        if (mentionActive) {
            const filtered = getFilteredMentions();

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex((prev) => {
                    if (e.key === 'ArrowDown') {
                        return prev < filtered.length - 1 ? prev + 1 : 0;
                    }
                    return prev > 0 ? prev - 1 : filtered.length - 1;
                });
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered[mentionIndex]) {
                    handleMentionSelect(filtered[mentionIndex]);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionActive(false);
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleEmojiSelect = (emoji) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            setText((prev) => prev + emoji);
            return;
        }
        const cursorPos = textarea.selectionStart;
        const before = text.substring(0, cursorPos);
        const after = text.substring(cursorPos);
        setText(before + emoji + after);
        setEmojiOpen(false);
        setTimeout(() => {
            const newPos = cursorPos + emoji.length;
            textarea.focus();
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const handleFileUpload = (e) => {
        const files = e.target.files;
        if (!files?.length) return;

        dispatch(chatActions.uploadAttachment(chatId, Array.from(files)));

        e.target.value = '';
    };

    return (
        <div className="chat-message-input-container">
            <div className="chat-message-input-wrapper">
                {mentionActive && (
                    <MentionDropdown
                        participants={participants}
                        filter={mentionFilter}
                        onSelect={handleMentionSelect}
                        activeIndex={mentionIndex}
                        currentUserId={currentUserId}
                    />
                )}
                <textarea
                    ref={textareaRef}
                    className="chat-message-textarea"
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Message"
                    rows={1}
                    aria-label="Type a message"
                />
                <div className="chat-input-actions-row">
                    <div className="chat-input-actions-left">
                        <button className="chat-input-action" onClick={handleAtButtonClick} title="Mention someone" type="button">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.4" />
                                <path
                                    d="M13 10v1.25a1.75 1.75 0 003.5 0V10a6.5 6.5 0 10-3.25 5.63"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            className="chat-input-action"
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach file"
                            type="button"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M17.95 9.28l-7.86 7.86a5 5 0 01-7.07-7.07l7.86-7.86a3.33 3.33 0 014.71 4.72l-7.86 7.85a1.67 1.67 0 01-2.36-2.35l7.07-7.07"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            accept="image/png,image/jpeg,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,video/mp4"
                            multiple
                        />
                        {!!uploadLoading?.length && (
                            <span style={{ fontSize: '12px', color: '#667085', marginLeft: '4px' }}>Uploading...</span>
                        )}
                        <div style={{ position: 'relative' }}>
                            <button className="chat-input-action" title="Emoji" type="button" onClick={() => setEmojiOpen(!emojiOpen)}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.4" />
                                    <path d="M7.5 8.5v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    <path d="M12.5 8.5v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    <path
                                        d="M7.5 12a3.5 3.5 0 005 0"
                                        stroke="currentColor"
                                        strokeWidth="1.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            {emojiOpen && <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setEmojiOpen(false)} />}
                        </div>
                    </div>
                    <button className="chat-send-button" onClick={handleSend} disabled={!text.trim()} type="button">
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};
